(ns hai.notes.card
  "The note card pieces every notes surface shares: the shared-with footer
   with its sharing dialog, and the compact read-only card surface list views
   render."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.notes.form :as form]
   [hai.sharing.components :as sharing.components]
   [hai.ui.components :as c]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private share-icon (icons/mui-icon :outlined :person_add))

;; Who the note is shared with and the share button — one row, whether it
;; sits in the read-only footer or at the start of the composer's.
(o/defstyled share-controls-styled :div
  {:display "flex" :align-items "center" :gap "10px" :min-width "0"}
  [:.shared-with-label
   {:font-size "13px" :font-weight "600"
    :color t/--note-kraft}]
  [:.can-share
   {:cursor "pointer"}]
  [:.share-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color t/--note-kraft
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :flex "0 0 auto"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--note-kraft-10}]
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  ;; Whatever class the button wears — its own on a card, the header's in a bar
  ;; — a disabled one swallows hover, so its tooltip is on the slot around it
  ;; and the button steps aside for the pointer.
  [:button:disabled {:opacity "0.4" :cursor "default" :background "transparent"
                     :pointer-events "none"}]
  [:.share-slot {:display "inline-flex"}])

;; The share controls sit at the left here too, where every composer's footer
;; has them.
(o/defstyled note-footer-styled :div
  {:display "flex" :flex-direction "column" :gap "12px"}
  [:.cutoff
   {:border-top-width "1px" :border-top-style "dashed" :border-top-color t/--note-sage-35}])

(defn share-dialog
  "Sharing dialog for a note. `!open?` controls it; closing calls
   `on-closed` so the host can re-resolve the note and surface freshly-added
   collaborators."
  [{:keys [note !open? on-closed]}]
  [c/DialogRoot
   {:open @!open?
    :onOpenChange (fn [opening?]
                    (reset! !open? opening?)
                    (when-not opening?
                      (on-closed)))}
   [c/DialogContent
    [sharing.components/sharing-dialog-title]
    [c/dialog-close-x]
    [c/DialogDescription "People with access to this note"]
    [sharing.components/permissions-table note]]])

(defn- others-shared-with
  "Everyone the note is shared with, the requester aside — what the share
   label lists. The owner holds no share, so a note shared with the requester
   alone lists nobody."
  [note]
  (let [requester-uuid (:user/uuid @(<s [:auth/identity]))]
    (remove #(= requester-uuid (:user/uuid %))
            (:entity/shared-with note))))

(defn shared-with
  "Who the note is shared with, the author aside — the label a composer carries
   at the start of its footer. Clicking it opens the Sharing dialog, the same
   one the header's ➤ opens; `share?` false leaves it as plain text, for a
   viewer who cannot share. Nothing at all while the note is shared with no
   one. `on-share-opened` (optional) fires as the dialog opens;
   `on-share-closed` re-resolves the note after it closes."
  [{:keys [note on-share-opened on-share-closed share?]
    :or {share? true}}]
  (r/with-let [!share-open? (r/atom false)]
    (let [shared-with (others-shared-with note)]
      (when (seq shared-with)
        [share-controls-styled
         [:span.shared-with-label
          {:on-click (fn [e]
                       (when share?
                         ;; A card around this one opens the note on any click
                         ;; it is handed; this click opens the dialog instead.
                         (.stopPropagation e)
                         (reset! !share-open? true)
                         (when on-share-opened (on-share-opened))))
           :class (when share? "can-share")}
          "Shared with "
          (table/label {:items shared-with
                        :xform (map sharing.components/shared-with-label)})]
         (when share?
           [share-dialog {:note      note
                          :!open?    !share-open?
                          :on-closed on-share-closed}])]))))

(defn share-button
  "The ➤ opening the Sharing dialog, for an editor's header bar — always there,
   disabled until the note exists, so the header doesn't jump when the first
   save lands. Same props as [[shared-with]]; each owns the dialog it opens, so
   neither has to reach the other's state. `class` is the button class the
   host's header sizes and colours (`bar-action-btn` by default); a surface
   with no header bar passes `share-btn` for the standalone treatment."
  [{:keys [note class on-share-opened on-share-closed]
    :or {class "bar-action-btn"}}]
  (r/with-let [!share-open? (r/atom false)]
    (let [saved? (some? (:note/uuid note))]
      [share-controls-styled
       [c/Tooltip {:content (if saved? "Share this note" "Save the note to share it")}
        [:span.share-slot
         [:button
          {:class class
           :type "button"
           :aria-label "Share this note"
           :disabled (not saved?)
           :on-click (fn []
                       (reset! !share-open? true)
                       (when on-share-opened (on-share-opened)))}
          [share-icon]]]]
       [share-dialog {:note      note
                      :!open?    !share-open?
                      :on-closed on-share-closed}]])))

(defn note-footer
  "The read-only surfaces' footer: [[shared-with]] under a dashed cutoff.
   Same props. Nothing at all, cutoff included, while there is nobody to
   list."
  [{:keys [note] :as props}]
  (when (seq (others-shared-with note))
    [note-footer-styled
     [:div.cutoff]
     [shared-with props]]))

(o/defstyled card-surface :div
  {:height "300px"
   :box-sizing "border-box"
   :border "1px solid transparent"
   :border-radius "4px"
   :color t/--note-kraft
   :background-color t/--sticky-yellow-light
   :box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 10px 24px rgba(45,62,42,0.14)"
   :padding "12px 16px 14px"
   :display "flex" :flex-direction "column" :gap "10px"
   :white-space "pre-wrap"
   :word-break "break-word"
   :transition "border-color 0.15s ease"}
  ;; Border on hover — pointer devices only, so a tap on touch doesn't leave
  ;; the card stuck in a highlighted state. The border sits transparent at
  ;; rest so nothing shifts.
  [:at-media {:hover "hover"}
   [:&:hover
    {:border-color t/--card-forest}]]
  ;; The whole surface opens the note, so it carries the cursor — the chips and
  ;; the ➤ / ⋮ that mean something else keep their own.
  [:&.clickable {:cursor "pointer"}]
  ;; The ➤ and the ⋮ together in the corner the editors' header bar keeps them
  ;; in, on a card that has no header bar. The box hugs them rather than
  ;; spanning the row: it stops the clicks it gets, so that the dialog and
  ;; menu they open — portaled, but bubbling up through React to here — never
  ;; open the note, and a row-wide box would take the whole top of the card
  ;; with it.
  [:.card-actions
   {:display "flex" :align-items "center" :align-self "flex-end"
    :flex "0 0 auto"
    :margin "-4px -8px -4px 0"}]
  ;; The text shrinks far more readily than the chip rows below (1000× shrink
  ;; weight), but never below two lines (2 × 14px × 1.5). Once it hits that
  ;; floor, the chips clip instead — so a metadata-heavy note can't squeeze the
  ;; text out of the card, and a long note keeps its chips intact.
  [:.scroll-wrap
   {:position "relative"
    :flex "1 1000 auto" :min-height "42px"
    :display "flex" :flex-direction "column"}]
  [:.card-scroll
   {:flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :user-select "none"
    :scrollbar-width "thin"
    :scrollbar-color "rgba(74,93,63,0.4) transparent"}
   ["&::-webkit-scrollbar" {:width "6px"}]
   ["&::-webkit-scrollbar-track" {:background-color "transparent"}]
   ["&::-webkit-scrollbar-thumb" {:background-color t/--note-sage-35
                                  :border-radius "3px"}]]
  [:.body {:font-size "14px" :line-height "1.5" :font-weight "500"}]
  [:.card-fade
   {:position "absolute" :left "0" :right "0" :bottom "0"
    :height "32px"
    ;; Tokens can't be (str …)-interpolated in a pure-cljs defstyled, so the
    ;; transparent end restates --sticky-yellow-light's value by hand.
    :background "linear-gradient(to bottom, rgba(250,244,223,0), var(--sticky-yellow-light))"
    :pointer-events "none"
    :opacity "0"
    :transition "opacity 0.15s ease"}
   [:&.visible {:opacity "1"}]]
  [:.extras-clip
   {:position "relative"
    :flex "0 1 auto" :min-height "22px"
    :overflow "hidden"}]
  [note-footer-styled
   {:flex "0 0 auto"}])

(defn- overflow-watch
  "Whether an element's content runs past its box, measured again whenever
   the box resizes: `observe!` takes the element from its ref, `check!`
   measures on demand, and `dispose!` goes in the `finally` of the
   `r/with-let` that made it."
  []
  (let [!overflow? (r/atom false)
        !element   (atom nil)
        check!     (fn []
                     (when-let [^js element @!element]
                       (reset! !overflow?
                               (< 2 (- (.-scrollHeight element)
                                       (.-scrollTop element)
                                       (.-clientHeight element))))))
        observer   (js/ResizeObserver. (fn [_] (check!)))]
    {:!overflow? !overflow?
     :check!     check!
     :observe!   (fn [element]
                   (when-let [^js previous @!element]
                     (.unobserve observer previous))
                   (reset! !element element)
                   (when element
                     (.observe observer element)
                     (check!)))
     :dispose!   #(.disconnect observer)}))

(defn card-text
  "The note's text, scrolling in the height the card leaves it, under a fade
   while more of it sits below the fold."
  [note]
  (r/with-let [{:keys [!overflow? check! observe! dispose!]} (overflow-watch)]
    [:div.scroll-wrap
     [:div.card-scroll
      ;; A new ref fn each render, so the text is measured again when it
      ;; changes: its box keeps its size then, and the observer stays quiet.
      {:ref       #(observe! %)
       :on-scroll (fn [_] (check!))}
      [:div.body (or (:note/text note) "")]]
     [:div.card-fade {:class (when @!overflow? "visible")}]]
    (finally (dispose!))))

(defn- card-chips
  "The note's tag and link chips, read-only, under a fade while more of them
   sit below the fold. Nothing when the note carries none."
  [{:keys [tags spaces link-urls contacts linked-notes linked-tasks]}]
  (r/with-let [{:keys [!overflow? observe! dispose!]} (overflow-watch)]
    (when (or (seq tags) (seq spaces) (seq link-urls) (seq contacts)
              (seq linked-notes) (seq linked-tasks))
      [:div.extras-clip
       {:ref #(observe! %)}
       ;; Read-only card (`:editing? false`) — `note-extras` renders space chips
       ;; without an adder or remove ×, so no space handlers are wired here.
       [form/note-extras
        {:editing?     false
         :tags         tags
         :spaces       spaces
         :link-urls    link-urls
         :contacts     contacts
         :linked-notes linked-notes
         :linked-tasks linked-tasks}]
       [:div.card-fade {:class (when @!overflow? "visible")}]])
    (finally (dispose!))))

(defn card
  "The compact read-only card surface — the note's text scrolling inside the
   fixed height ([[card-text]]), the tag/link chips, and who it is shared with
   pinned under them. `on-body-click` fires anywhere on the card — the way
   pressing a task row opens it — save for what already means something else:
   the ➤ and the ⋮, and the chips and share label, which stop the click
   themselves. The text is a small part of a short card, so anything narrower
   is a target to hunt for. The card has no title bar, so its ➤ and its ⋮
   (Delete, when `on-delete` is given) share the top-right corner the editors'
   header keeps them in. `share?` gates the ➤."
  [{:keys [note tags spaces link-urls contacts linked-notes linked-tasks
           on-body-click on-delete share? on-share-closed]
    :or   {share? true}}]
  [card-surface
   {:class (when on-body-click "clickable")
    :on-click on-body-click}
   (when (or share? on-delete)
     [:div.card-actions
      {:on-click #(.stopPropagation %)}
      (when share?
        [share-button {:note            note
                       :class           "share-btn"
                       :on-share-closed on-share-closed}])
      (when on-delete
        [kebab/menu
         {:on-delete      on-delete
          :delete-label   "Delete note"
          :confirm-prompt "Delete this note?"
          :aria-label     "Note actions"}])])
   [card-text note]
   [card-chips {:tags         tags
                :spaces       spaces
                :link-urls    link-urls
                :contacts     contacts
                :linked-notes linked-notes
                :linked-tasks linked-tasks}]
   [note-footer
    {:note            note
     :share?          share?
     :on-share-closed on-share-closed}]])
